/* ore-table — shadow DOM (proxy/mirror).
 *
 * Shadow tree (managed by table.ts):
 *   :host                  display:block   — token host (tableBaseMixin)
 *   └─ .scroll-container   overflow:auto   — sticky / max-height context
 *       └─ table           display:table   — native table semantics
 *           ├─ caption                     — optional caption bar
 *           ├─ thead                       — rows built from ore-tr[head]
 *           ├─ tbody                       — rows built from plain ore-tr
 *           └─ tfoot                       — rows built from ore-tr[foot]
 *               └─ tr > th / td            — mirrored from ore-th / ore-td
 *
 * Light DOM (consumer):
 *   <ore-tr head><ore-th>…</ore-th></ore-tr>
 *   <ore-tr><ore-td>…</ore-td></ore-tr>
 *   <ore-tr foot><ore-td>…</ore-td></ore-tr>
 *
 * tableBaseMixin('table') handles: :host tokens, .scroll-container,
 * table width/collapse, loading state, size variants, fullwidth, bordered,
 * and sticky scroll-container activation.
 * This file handles: caption, section backgrounds, body tr/th/td, and variants.
 * No ::slotted() needed — the shadow tree owns all rendered th/td elements.
 */

@layer refine.base {
  :host {
    border: none;
    border-radius: 0;
    box-shadow: var(--table-shadow, none);
  }

  /* ── Caption ─────────────────────────────────────────────────────────── */

  caption {
    padding: var(--size-3);
    font-size: var(--text-xs);
    font-weight: var(--font-semibold);
    color: var(--color-contrast-500);
    text-align: start;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    caption-side: top;
    background: var(--_header-bg);
  }

  /* ── Section backgrounds ─────────────────────────────────────────────── */

  thead,
  tfoot {
    background: var(--_header-bg);
  }

  thead {
    border-block-end: var(--border) solid var(--_border-color);
  }

  tfoot {
    border-block-start: var(--border) solid var(--_border-color);
  }

  /* ── Body rows ───────────────────────────────────────────────────────── */

  tbody tr {
    border-block-end: var(--border) solid var(--_border-color);
    transition: background var(--transition-fast);
  }

  tbody tr:hover {
    background: var(--_row-hover-bg);
  }

  /* ── Cells ───────────────────────────────────────────────────────────── */

  th,
  td {
    padding: var(--_cell-padding-y) var(--_cell-padding-x);
    font-size: var(--_font-size);
    font-weight: normal;
    vertical-align: middle;
    color: var(--color-contrast-800);
    text-align: start;
  }

  /* ── Header cells ────────────────────────────────────────────────────── */

  thead th,
  thead td,
  tfoot th,
  tfoot td {
    font-size: var(--text-xs);
    font-weight: var(--font-semibold);
    color: var(--color-contrast-500);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
    user-select: none;
  }
}

@layer refine.variants {
  /* ── Bordered: vertical cell separators ─────────────────────────────── */

  :host([bordered]) th,
  :host([bordered]) td {
    border-inline-end: var(--border) solid var(--_border-color);
  }

  :host([bordered]) th:last-child,
  :host([bordered]) td:last-child {
    border-inline-end: none;
  }

  /* ── Striped ─────────────────────────────────────────────────────────── */

  :host([striped]) tbody tr:nth-child(even) {
    background: var(--_stripe-bg);
  }

  /* ── Sticky header ───────────────────────────────────────────────────── */

  /* tableBaseMixin activates overflow-y:auto on .scroll-container when
     [sticky] is present. position:sticky on thead is relative to that context. */

  :host([sticky]) thead {
    position: sticky;
    top: 0;
    z-index: 2;
    background: var(--table-sticky-header-bg, color-mix(in oklch, var(--_header-bg) 94%, transparent));

    /* border-collapse merges the bottom border into the cell below, which
       scrolls away under the sticky header. Use box-shadow instead — it
       travels with the element regardless of border-collapse. */
    box-shadow: inset 0 calc(var(--border) * -1) 0 var(--_border-color);
    backdrop-filter: blur(var(--table-sticky-blur, var(--blur-md))) saturate(140%);
  }
}
